import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  useCreateUserSessionMutation,
  type EntitiesCreateUserSessionResponse,
  type EntitiesUserResponse
} from '@axilon-apps/domains-identity/state'

import { Button } from '../../../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../../components/ui/dialog'
import { Input } from '../../../../components/ui/input'
import { Label } from '../../../../components/ui/label'
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group'
import { apiKeyErrorMessage } from './api-key-error'
import { apiKeyExpiryValue, type ApiKeyExpiryChoice } from './expiry'

interface RotateApiKeyDialogProps {
  apiKey: EntitiesUserResponse | null
  onOpenChange: (open: boolean) => void
  onIssued: (token: EntitiesCreateUserSessionResponse) => void
}

// Rotation mints a new session for the service-account user; the previous
// token stays valid until it is revoked from the table.
export function RotateApiKeyDialog({ apiKey, onOpenChange, onIssued }: RotateApiKeyDialogProps) {
  const [expiryChoice, setExpiryChoice] = useState<ApiKeyExpiryChoice>('1-year')
  const [customDate, setCustomDate] = useState('')
  const [error, setError] = useState('')
  const [createSession, rotateState] = useCreateUserSessionMutation()
  const expiry = useMemo(
    () => apiKeyExpiryValue(expiryChoice, customDate),
    [customDate, expiryChoice]
  )

  useEffect(() => {
    if (!apiKey) return
    setExpiryChoice('1-year')
    setCustomDate('')
    setError('')
  }, [apiKey])

  const submit = async () => {
    const submissionExpiry = apiKeyExpiryValue(expiryChoice, customDate)
    if (!apiKey || !submissionExpiry.expiresInSeconds) {
      setError(submissionExpiry.error || 'Choose a valid expiry.')
      return
    }
    setError('')
    try {
      const token = await createSession({
        id: apiKey.id,
        body: { expiresInSeconds: submissionExpiry.expiresInSeconds }
      }).unwrap()
      rotateState.reset()
      onOpenChange(false)
      onIssued(token)
    } catch (requestError) {
      setError(apiKeyErrorMessage(requestError))
    }
  }

  return (
    <Dialog
      open={apiKey !== null}
      onOpenChange={(open) => {
        if (!open && !rotateState.isLoading) onOpenChange(false)
      }}
    >
      <DialogContent className="max-h-[calc(100vh-2rem)] min-w-0 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Rotate {apiKey?.name}</DialogTitle>
          <DialogDescription>
            Create a replacement token. Existing tokens stay valid until you revoke them.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={expiryChoice}
          onValueChange={(value) => {
            setExpiryChoice(value as ApiKeyExpiryChoice)
            setError('')
          }}
          className="grid gap-2 sm:grid-cols-3"
        >
          <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3">
            <RadioGroupItem value="90-days" />
            90 days
          </Label>
          <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3">
            <RadioGroupItem value="1-year" />1 year
          </Label>
          <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3">
            <RadioGroupItem value="custom" />
            Custom
          </Label>
        </RadioGroup>
        {expiryChoice === 'custom' ? (
          <Input
            aria-label="Custom expiry date"
            type="date"
            value={customDate}
            onChange={(event) => {
              setCustomDate(event.target.value)
              setError('')
            }}
          />
        ) : null}
        {expiry.expiresAt && !expiry.error ? (
          <p className="text-xs text-ink-3">Expires {expiry.expiresAt.toLocaleString()}.</p>
        ) : null}
        {error ? (
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            {error}
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={rotateState.isLoading}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={rotateState.isLoading || Boolean(expiry.error)}
            onClick={() => void submit()}
          >
            {rotateState.isLoading ? <Loader2 className="animate-spin" /> : null}
            Rotate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
